'use client';

import { useEffect, useRef, useState } from 'react';
import { useLocale } from 'next-intl';
import { useParams, usePathname } from 'next/navigation';

import { usePostHogSupport } from '@/hooks/use-posthog-support';
import { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import type {
  SupportTicket,
  UsePostHogSupportResult,
} from '@/lib/posthog-support/types';

import {
  PostHogSupportLoading,
  PostHogSupportSignedOut,
  PostHogSupportUnavailable,
} from './posthog-support-boundary';
import { PostHogSupportComposer } from './posthog-support-composer';
import { PostHogSupportHeader } from './posthog-support-header';
import { PostHogSupportIntentChooser } from './posthog-support-intent-chooser';
import {
  getPostHogSupportContentWidth,
  getPostHogSupportHorizontalPadding,
} from './posthog-support-layout';
import { PostHogSupportRequests } from './posthog-support-requests';
import { PostHogSupportSent } from './posthog-support-sent';
import {
  buildContextualSupportMessage,
  getInitialPostHogSupportView,
  getSupportErrorMessage,
  getSupportMessageSubject,
  getSupportPageContext,
  resolveDraftAfterSend,
  type SupportIntent,
} from './posthog-support-state';
import { PostHogSupportTicketView } from './posthog-support-ticket';

export type PostHogSupportDraftController = {
  blockingWork: boolean;
  intent: SupportIntent | null;
  newMessage: string;
  replyMessages: Record<string, string>;
  setIntent: (intent: SupportIntent | null) => void;
  setBlockingWork: (blocking: boolean) => void;
  setNewMessage: (message: string) => void;
  setReplyMessage: (ticketId: string, message: string) => void;
};

type PostHogSupportProps = {
  compact?: boolean;
  dialog?: boolean;
  initialIntent?: SupportIntent;
  initialTicketId?: string;
  draftController?: PostHogSupportDraftController;
};

export function PostHogSupport(props: PostHogSupportProps) {
  const { user, isLoading } = useAuth();
  const draftOwnerKey = isLoading ? 'loading' : (user?.userId ?? 'signed-out');
  return <PostHogSupportForOwner key={draftOwnerKey} {...props} />;
}

function PostHogSupportForOwner(props: PostHogSupportProps) {
  const support = usePostHogSupport({ ticketId: props.initialTicketId });
  return <PostHogSupportPanel {...props} support={support} />;
}

export function PostHogSupportPanel({
  compact = false,
  dialog = false,
  draftController,
  initialIntent,
  initialTicketId,
  support,
}: PostHogSupportProps & { support: UsePostHogSupportResult }) {
  const locale = useLocale();
  const copy = getPostHogSupportCopy(locale);
  const pathname = usePathname();
  const params = useParams<{ orgSlug?: string | string[] }>();
  const [localBlockingWork, setLocalBlockingWork] = useState(false);
  const [localIntent, setLocalIntent] = useState<SupportIntent | null>(
    initialIntent ?? null
  );
  const [localNewMessage, setLocalNewMessage] = useState('');
  const [localReplyMessages, setLocalReplyMessages] = useState<
    Record<string, string>
  >({});
  const [view, setView] = useState(() =>
    getInitialPostHogSupportView({ initialIntent, initialTicketId })
  );
  const [submittedTicket, setSubmittedTicket] = useState<SupportTicket | null>(
    null
  );
  const [submittedRequestSubject, setSubmittedRequestSubject] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [supportErrorContext, setSupportErrorContext] = useState<
    'load' | 'send'
  >('load');
  const [selectingTicketId, setSelectingTicketId] = useState<string | null>(
    null
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshingRequests, setIsRefreshingRequests] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const rootRef = useRef<HTMLElement>(null);
  const blockingWork = draftController?.blockingWork ?? localBlockingWork;
  const intent = draftController?.intent ?? localIntent;
  const newMessage = draftController?.newMessage ?? localNewMessage;
  const replyMessages = draftController?.replyMessages ?? localReplyMessages;
  const activeTicket =
    support.tickets.find((ticket) => ticket.id === support.activeTicketId) ??
    (submittedTicket?.id === support.activeTicketId ? submittedTicket : null);
  const activeReply = support.activeTicketId
    ? (replyMessages[support.activeTicketId] ?? '')
    : '';
  const context = getSupportPageContext({
    pathname,
    orgSlug: params?.orgSlug,
  });

  useEffect(() => {
    if (support.status === 'loading') return;
    rootRef.current
      ?.querySelector<HTMLElement>('[data-support-view-heading]')
      ?.focus();
  }, [support.status, view]);

  const updateIntent = (nextIntent: SupportIntent | null) => {
    setLocalIntent(nextIntent);
    draftController?.setIntent(nextIntent);
  };

  const updateBlockingWork = (blocking: boolean) => {
    setLocalBlockingWork(blocking);
    draftController?.setBlockingWork(blocking);
  };

  const updateNewMessage = (message: string) => {
    setSubmitError(null);
    setLocalNewMessage(message);
    draftController?.setNewMessage(message);
  };

  const updateReplyMessage = (ticketId: string, message: string) => {
    setSubmitError(null);
    setLocalReplyMessages((current) => ({ ...current, [ticketId]: message }));
    draftController?.setReplyMessage(ticketId, message);
  };

  const handleStartNew = () => {
    support.startNew();
    setSubmitError(null);
    setSubmittedTicket(null);
    setSubmittedRequestSubject('');
    setView(intent ? 'compose' : 'choose');
  };

  const handleSelectIntent = (nextIntent: SupportIntent) => {
    updateIntent(nextIntent);
    if (nextIntent !== 'problem') updateBlockingWork(false);
    setSubmitError(null);
    setView('compose');
  };

  const handleSubmitNew = async () => {
    if (!intent || !newMessage.trim() || support.isSending) return;
    const requestSubject = getSupportMessageSubject(newMessage);
    setSubmitError(null);
    setSupportErrorContext('send');
    try {
      const ticket = await support.send({
        message: buildContextualSupportMessage({
          message: newMessage,
          context,
          intentLabel:
            intent === 'problem'
              ? copy.reportProblem
              : intent === 'question'
                ? copy.askTeam
                : copy.shareIdea,
          blockingWorkLabel:
            intent === 'problem' && blockingWork
              ? copy.blockingWork
              : undefined,
          includedDetailsLabel: copy.includedDetails,
          pageLabel: copy.pageLabel,
          workspaceLabel: copy.workspaceLabel,
        }),
        newTicket: true,
        subject: requestSubject,
      });
      updateNewMessage(
        resolveDraftAfterSend({ draft: newMessage, result: ticket })
      );
      if (!ticket) {
        return;
      }
      updateIntent(null);
      updateBlockingWork(false);
      setSubmittedTicket(ticket);
      setSubmittedRequestSubject(requestSubject);
      setView('sent');
    } catch {
      setSubmitError(copy.failedToSend);
    }
  };

  const handleSelectTicket = async (ticketId: string) => {
    if (selectingTicketId !== null) return;
    setSupportErrorContext('load');
    setSelectingTicketId(ticketId);
    setSubmitError(null);
    try {
      await support.selectTicket(ticketId);
      setView('ticket');
    } catch {
      setSubmitError(copy.failedToLoad);
    } finally {
      setSelectingTicketId(null);
    }
  };

  const handleSubmitReply = async () => {
    const ticketId = support.activeTicketId;
    if (!ticketId || !activeReply.trim() || support.isSending) return;
    setSubmitError(null);
    setSupportErrorContext('send');
    try {
      const ticket = await support.send({ message: activeReply });
      updateReplyMessage(
        ticketId,
        resolveDraftAfterSend({ draft: activeReply, result: ticket })
      );
      if (!ticket) {
        return;
      }
    } catch {
      setSubmitError(copy.failedToSend);
    }
  };

  const handleLoadMore = async () => {
    setSupportErrorContext('load');
    setIsLoadingMore(true);
    try {
      await support.loadMoreTickets();
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleRetry = async () => {
    setSubmitError(null);
    setSupportErrorContext('load');
    setIsRetrying(true);
    try {
      await support.refresh();
    } finally {
      setIsRetrying(false);
    }
  };

  const handleRetryTicket = async () => {
    if (!support.activeTicketId) return;
    setSupportErrorContext('load');
    setIsRetrying(true);
    try {
      await support.selectTicket(support.activeTicketId);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleOpenRequests = async () => {
    if (isRefreshingRequests) return;
    setSubmitError(null);
    setSupportErrorContext('load');
    setView('requests');
    setIsRefreshingRequests(true);
    try {
      await support.refresh();
    } finally {
      setIsRefreshingRequests(false);
    }
  };

  const renderContent = () => {
    if (support.status === 'loading') {
      return <PostHogSupportLoading copy={copy} />;
    }
    if (support.status === 'unavailable') {
      return (
        <PostHogSupportUnavailable
          copy={copy}
          isRetrying={isRetrying}
          onRetry={handleRetry}
        />
      );
    }
    if (support.status === 'signed-out') {
      return <PostHogSupportSignedOut copy={copy} returnTo={pathname || '/'} />;
    }
    if (view === 'compose' && intent) {
      const composerError =
        submitError ??
        (supportErrorContext === 'send'
          ? getSupportErrorMessage(copy, support.error, 'failedToSend')
          : null);
      return (
        <PostHogSupportComposer
          blockingWork={blockingWork}
          compact={compact}
          copy={copy}
          context={context}
          error={composerError}
          intent={intent}
          isSending={support.isSending}
          message={newMessage}
          onBack={() => {
            updateIntent(null);
            updateBlockingWork(false);
            setView('choose');
          }}
          onBlockingWorkChange={updateBlockingWork}
          onMessageChange={updateNewMessage}
          onSubmit={handleSubmitNew}
        />
      );
    }
    if (view === 'requests') {
      const requestsError =
        submitError ??
        getSupportErrorMessage(copy, support.error, 'failedToLoad');
      return (
        <>
          {requestsError && support.tickets.length > 0 ? (
            <div
              className={cn(
                'pt-3',
                getPostHogSupportHorizontalPadding(compact)
              )}
            >
              <div className={getPostHogSupportContentWidth(compact)}>
                <p
                  role="alert"
                  className="text-destructive text-xs text-pretty"
                >
                  {requestsError}
                </p>
              </div>
            </div>
          ) : null}
          <PostHogSupportRequests
            compact={compact}
            copy={copy}
            error={requestsError}
            hasMore={support.hasMoreTickets}
            isLoadingMore={isLoadingMore}
            isRetrying={isRetrying}
            locale={locale}
            onLoadMore={() => void handleLoadMore()}
            onNewRequest={handleStartNew}
            onRetry={() => void handleRetry()}
            onSelect={(ticketId) => void handleSelectTicket(ticketId)}
            selectingTicketId={selectingTicketId}
            tickets={support.tickets}
          />
        </>
      );
    }
    if (view === 'ticket') {
      const ticketError =
        supportErrorContext === 'send'
          ? getSupportErrorMessage(copy, support.error, 'failedToSend')
          : null;
      const ticketReadError =
        supportErrorContext === 'load'
          ? getSupportErrorMessage(copy, support.error, 'failedToLoad')
          : null;
      return (
        <PostHogSupportTicketView
          compact={compact}
          copy={copy}
          error={submitError ?? ticketError}
          isSending={support.isSending}
          isRetrying={isRetrying}
          locale={locale}
          messages={support.messages}
          onBack={() => void handleOpenRequests()}
          onRetry={handleRetryTicket}
          onReplyChange={(message) => {
            if (support.activeTicketId) {
              updateReplyMessage(support.activeTicketId, message);
            }
          }}
          onSubmitReply={handleSubmitReply}
          readError={!submitError ? ticketReadError : null}
          reply={activeReply}
          ticket={activeTicket}
        />
      );
    }
    if (view === 'sent' && submittedTicket) {
      return (
        <PostHogSupportSent
          copy={copy}
          isSelecting={selectingTicketId !== null}
          onViewRequest={() => {
            if (selectingTicketId !== null) return;
            void handleSelectTicket(submittedTicket.id);
          }}
          subject={submittedRequestSubject}
        />
      );
    }
    return (
      <PostHogSupportIntentChooser
        compact={compact}
        copy={copy}
        onChoose={handleSelectIntent}
      />
    );
  };

  return (
    <section
      ref={rootRef}
      className="bg-background flex h-full min-h-0 w-full flex-col overflow-hidden"
      aria-label={copy.title}
    >
      <PostHogSupportHeader
        actionsAvailable={support.status === 'ready'}
        compact={compact}
        copy={copy}
        dialog={dialog}
        isRefreshingRequests={isRefreshingRequests}
        onNewRequest={handleStartNew}
        onOpenRequests={() => void handleOpenRequests()}
        view={view}
      />
      {renderContent()}
    </section>
  );
}
